import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { ForceGraph2D } from 'react-force-graph';
import { ArrowLeft, ExternalLink, X } from 'lucide-react';
import TweetCard from './TweetCard';
import RoamQuoteCard from './RoamQuoteCard';
import { isRoamSource } from '../utils';

const CLUSTER_COLORS = [
  '#f05a7e', '#52c9a3', '#8b5cf6', '#f59e0b', '#3b9fe0',
  '#e63b6e', '#38bdf8', '#f97316', '#818cf8', '#06b6d4',
];

const SOURCE_COLORS = {
  bookmarks: '#1da1f2',
  likes:     '#f43f5e',
  roam:      '#8b5cf6',
  reddit:    '#ff6314',
};

const STOPWORDS = new Set([
  'time', 'don', 'life', 'make', 'want', 'world', 've', 'love', 'feel',
  'thing', 'need', 'said', 'use', 'people', 'good', 'going', 'also',
  'didn', 'back', 'year', 'new', 'old', 'just', 'know', 'think', 'way',
  'like', 'get', 'do', 'got', 'made', 'day', 'days',
]);

const MAX_TWEET_NODES = 50;
const HEIGHT = 460;

// ─── Dark mode detection ───────────────────────────────────────────────────

function useDarkMode() {
  const [isDark, setIsDark] = useState(
    () => document.documentElement.classList.contains('dark')
  );
  useEffect(() => {
    const mo = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains('dark'))
    );
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => mo.disconnect();
  }, []);
  return isDark;
}

// ─── Graph data builders ───────────────────────────────────────────────────

function buildClusterGraphData(clusters) {
  const entries = Object.entries(clusters).filter(
    ([id, c]) => id !== '-1' && c.topic_label && !c.topic_label.includes('unknown')
  );

  const nodes = entries.map(([id, cluster]) => ({
    id,
    label: cluster.topic_label,
    // First 3 words, preserving emojis, stripping punctuation
    shortLabel: cluster.topic_label.replace(/[^\w\s\u{1F000}-\u{1FFFF}]/gu, '').trim().split(/\s+/).slice(0, 3).join(' '),
    size: Math.max(5, Math.sqrt(cluster.size) * 0.55),
    color: CLUSTER_COLORS[parseInt(id) % CLUSTER_COLORS.length],
    terms: cluster.topic_terms || [],
    tweetCount: cluster.size,
  }));

  const links = [];
  for (let i = 0; i < nodes.length; i++) {
    const termsI = nodes[i].terms.filter(t => !STOPWORDS.has(t));
    for (let j = i + 1; j < nodes.length; j++) {
      const termsJ = new Set(nodes[j].terms.filter(t => !STOPWORDS.has(t)));
      const shared = termsI.filter(t => termsJ.has(t)).length;
      if (shared > 0) links.push({ source: nodes[i].id, target: nodes[j].id, value: shared });
    }
  }

  return { nodes, links };
}

function buildTweetGraphData(cluster, clusterColor, similarityIndex, tweetMap) {
  const allRefs = cluster.tweet_refs || [];
  const clusterIdSet = new Set(allRefs.map(r => r.id));

  const degreeMap = new Map();
  if (similarityIndex) {
    for (const ref of allRefs) {
      const entry = similarityIndex[ref.id];
      let degree = 0;
      if (entry) {
        for (const sim of entry.similar_tweets || []) {
          if (clusterIdSet.has(sim.tweet_id)) degree++;
        }
      }
      degreeMap.set(ref.id, degree);
    }
  }

  const sortedRefs = [...allRefs]
    .sort((a, b) => (degreeMap.get(b.id) || 0) - (degreeMap.get(a.id) || 0))
    .slice(0, MAX_TWEET_NODES);

  const visibleIds = new Set(sortedRefs.map(r => r.id));

  const nodes = sortedRefs.map(ref => {
    const tweet = tweetMap.get(ref.id);
    const text = tweet?.text || tweet?.full_text || tweet?.content || '';
    const handle = tweet?.user?.screen_name || null;
    return {
      id: ref.id,
      source: ref.source,
      color: SOURCE_COLORS[ref.source] || clusterColor,
      size: 5,
      text: text.substring(0, 160),
      handle,
      tweetUrl: handle
        ? `https://x.com/${handle}/status/${ref.id}`
        : /^\d+$/.test(ref.id) ? `https://x.com/i/web/status/${ref.id}` : null,
    };
  });

  const links = [];
  const seen = new Set();
  if (similarityIndex) {
    for (const node of nodes) {
      const entry = similarityIndex[node.id];
      if (!entry) continue;
      for (const sim of entry.similar_tweets || []) {
        if (!visibleIds.has(sim.tweet_id)) continue;
        const key = [node.id, sim.tweet_id].sort().join('|');
        if (seen.has(key)) continue;
        seen.add(key);
        links.push({ source: node.id, target: sim.tweet_id, value: sim.similarity_score });
      }
    }
  }

  return { nodes, links };
}

// ─── Component ────────────────────────────────────────────────────────────

export default function ClusterGraph({ clusters, selectedTopic, onTopicSelect, allTweets }) {
  const containerRef = useRef();
  const graphRef = useRef();
  const [containerWidth, setContainerWidth] = useState(600);
  const [hovered, setHovered] = useState(null);
  const isDark = useDarkMode();

  const [mode, setMode] = useState('clusters');
  const [drillClusterId, setDrillClusterId] = useState(null);
  const [similarityIndex, setSimilarityIndex] = useState(null);
  const [loadingSimilarity, setLoadingSimilarity] = useState(false);
  const [selectedTweet, setSelectedTweet] = useState(null);

  // Colors derived from theme
  const bgColor    = isDark ? '#0f172a' : '#f8fafc';
  const linkColor  = isDark ? '#ffffff45' : '#00000040';
  const labelColor = isDark ? '#e2e8f0'  : '#1e293b';

  // Responsive container width
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries =>
      setContainerWidth(entries[0].contentRect.width || 600)
    );
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Tune d3 forces after each graph remount, then zoom to fit
  const handleEngineStop = useCallback(() => {
    graphRef.current?.zoomToFit(300, 50);
  }, []);

  useEffect(() => {
    if (!graphRef.current) return;
    const fg = graphRef.current;
    if (mode === 'clusters') {
      fg.d3Force('charge').strength(-120);
      fg.d3Force('link').distance(55);
    } else {
      fg.d3Force('charge').strength(-60);
      fg.d3Force('link').distance(28);
    }
    fg.d3ReheatSimulation();
  }, [mode, drillClusterId]);

  // Tweet lookup map
  const tweetMap = useMemo(() => {
    if (!allTweets) return new Map();
    return new Map(allTweets.map(t => [t.id, t]));
  }, [allTweets]);

  const loadSimilarity = useCallback(() => {
    if (similarityIndex || loadingSimilarity) return;
    setLoadingSimilarity(true);
    fetch('/exports/insights/similarity_index.json')
      .then(r => r.json())
      .then(data => setSimilarityIndex(data.similarity_index))
      .catch(e => console.error('Failed to load similarity index', e))
      .finally(() => setLoadingSimilarity(false));
  }, [similarityIndex, loadingSimilarity]);

  // ── Graph data ─────────────────────────────────────────────────────────

  const clusterGraphData = useMemo(() => buildClusterGraphData(clusters), [clusters]);

  const tweetGraphData = useMemo(() => {
    if (mode !== 'tweets' || !drillClusterId || !clusters[drillClusterId]) return null;
    const cluster = clusters[drillClusterId];
    const color = CLUSTER_COLORS[parseInt(drillClusterId) % CLUSTER_COLORS.length];
    return buildTweetGraphData(cluster, color, similarityIndex, tweetMap);
  }, [mode, drillClusterId, clusters, similarityIndex, tweetMap]);

  const activeGraphData = mode === 'clusters'
    ? clusterGraphData
    : (tweetGraphData || { nodes: [], links: [] });

  // ── Node painters ──────────────────────────────────────────────────────

  const clusterNodePaint = useCallback((node, ctx, globalScale) => {
    if (node.x == null) return;
    const isSelected = node.id === selectedTopic;
    const isHovered = hovered?.id === node.id;
    const r = node.size;

    // Selection / hover ring
    if (isSelected || isHovered) {
      ctx.beginPath();
      ctx.arc(node.x, node.y, r + 5, 0, 2 * Math.PI);
      ctx.fillStyle = node.color + '28';
      ctx.fill();
    }

    // Circle
    ctx.beginPath();
    ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
    ctx.fillStyle = node.color + (isSelected ? 'dd' : '99');
    ctx.fill();
    ctx.strokeStyle = node.color;
    ctx.lineWidth = (isSelected ? 2 : 1) / globalScale;
    ctx.stroke();

    // Label below with text shadow for contrast
    const fontSize = Math.max(8, Math.min(12, 11 / globalScale));
    ctx.font = `500 ${fontSize}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';

    const textY = node.y + r + 4 / globalScale;
    ctx.shadowColor = isDark ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.95)';
    ctx.shadowBlur = 5;
    ctx.fillStyle = labelColor;
    ctx.fillText(node.shortLabel, node.x, textY);
    ctx.shadowBlur = 0;
  }, [selectedTopic, hovered, isDark, labelColor]);

  const tweetNodePaint = useCallback((node, ctx) => {
    if (node.x == null) return;
    const isHovered = hovered?.id === node.id;
    const r = isHovered ? node.size + 2 : node.size;

    if (isHovered) {
      ctx.beginPath();
      ctx.arc(node.x, node.y, r + 4, 0, 2 * Math.PI);
      ctx.fillStyle = node.color + '30';
      ctx.fill();
    }

    ctx.beginPath();
    ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
    ctx.fillStyle = node.color + (isHovered ? 'ff' : 'bb');
    ctx.fill();
  }, [hovered]);

  const nodePointerAreaPaint = useCallback((node, color, ctx) => {
    if (node.x == null) return;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(node.x, node.y, node.size + 6, 0, 2 * Math.PI);
    ctx.fill();
  }, []);

  // ── Interaction ────────────────────────────────────────────────────────

  const handleClusterClick = useCallback((node) => {
    setDrillClusterId(node.id);
    setMode('tweets');
    setHovered(null);
    loadSimilarity();
  }, [loadSimilarity]);

  const handleTweetClick = useCallback((node) => {
    const tweet = tweetMap.get(node.id);
    if (tweet) setSelectedTweet(tweet);
  }, [tweetMap]);

  const handleBack = useCallback(() => {
    setMode('clusters');
    setDrillClusterId(null);
    setHovered(null);
  }, []);

  const drillCluster = drillClusterId ? clusters[drillClusterId] : null;
  const drillColor   = drillClusterId ? CLUSTER_COLORS[parseInt(drillClusterId) % CLUSTER_COLORS.length] : '#1da1f2';

  // Theme-aware UI chrome
  const chromeBg   = isDark ? 'rgba(15,23,42,0.92)' : 'rgba(248,250,252,0.92)';
  const chromeText = isDark ? 'text-gray-400 hover:text-gray-100' : 'text-gray-500 hover:text-gray-900';
  const hintText   = isDark ? 'text-gray-500' : 'text-gray-400';
  const tipBg      = isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900';
  const tipSub     = isDark ? 'text-gray-400' : 'text-gray-500';

  return (
    <div
      ref={containerRef}
      className="relative w-full rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700"
      style={{ height: HEIGHT, background: bgColor }}
    >
      {/* Cluster hint */}
      {mode === 'clusters' && (
        <div className={`absolute top-3 right-3 z-10 text-xs pointer-events-none ${hintText}`}>
          click a cluster to explore its tweets
        </div>
      )}

      {/* Tweet mode toolbar */}
      {mode === 'tweets' && drillCluster && (
        <div
          className="absolute top-0 left-0 right-0 z-10 flex items-center gap-3 px-3 py-2"
          style={{ background: `linear-gradient(to bottom, ${chromeBg}, transparent)` }}
        >
          <button onClick={handleBack} className={`flex items-center gap-1 text-xs transition-colors ${chromeText}`}>
            <ArrowLeft className="w-3 h-3" />
            All clusters
          </button>
          <div className="flex-1 min-w-0">
            <span className="text-xs font-semibold truncate" style={{ color: drillColor }}>
              {drillCluster.topic_label}
            </span>
            <span className={`text-xs ml-2 ${hintText}`}>
              {loadingSimilarity
                ? 'loading connections…'
                : `top ${Math.min(MAX_TWEET_NODES, drillCluster.tweet_refs?.length || 0)} of ${drillCluster.size} items`}
            </span>
          </div>
          <button
            onClick={() => onTopicSelect(drillClusterId)}
            className={`flex items-center gap-1 text-xs transition-colors flex-shrink-0 ${chromeText}`}
          >
            <ExternalLink className="w-3 h-3" />
            view list
          </button>
        </div>
      )}

      {/* Source legend (tweet mode) */}
      {mode === 'tweets' && (
        <div className="absolute bottom-3 left-3 z-10 flex gap-3 pointer-events-none">
          {Object.entries(SOURCE_COLORS).map(([src, color]) => (
            <div key={src} className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full" style={{ background: color }} />
              <span className={`text-xs ${hintText}`}>{src}</span>
            </div>
          ))}
        </div>
      )}

      {/* Hover tooltip */}
      {hovered && (
        <div
          className={`absolute top-10 left-3 z-10 border rounded-lg px-3 py-2 pointer-events-none shadow-md text-xs ${tipBg}`}
          style={{ maxWidth: 260 }}
        >
          {mode === 'clusters' ? (
            <>
              <div className="font-semibold leading-tight">{hovered.label}</div>
              <div className={`mt-1 ${tipSub}`}>{hovered.tweetCount?.toLocaleString()} items</div>
              {hovered.terms?.length > 0 && (
                <div className={`mt-1 ${tipSub}`}>{hovered.terms.slice(0, 4).join(' · ')}</div>
              )}
            </>
          ) : (
            <>
              {hovered.handle && <div className={`mb-1 ${tipSub}`}>@{hovered.handle}</div>}
              <div className="leading-snug">{hovered.text || '(no text)'}</div>
              <div className={`mt-1 ${tipSub}`}>click to preview</div>
            </>
          )}
        </div>
      )}

      {/* Tweet preview modal */}
      {selectedTweet && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSelectedTweet(null)} />
          <div className="relative z-10 w-full max-w-xl max-h-[80vh] overflow-y-auto rounded-xl shadow-2xl">
            <button
              onClick={() => setSelectedTweet(null)}
              className="absolute top-3 right-3 z-20 p-1.5 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <X className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            </button>
            {isRoamSource(selectedTweet.source)
              ? <RoamQuoteCard quote={selectedTweet} />
              : <TweetCard tweet={selectedTweet} />
            }
          </div>
        </div>
      )}

      {/* Force graph — key forces remount on mode/cluster change so simulation resets cleanly */}
      <ForceGraph2D
        key={`${mode}-${drillClusterId ?? 'all'}`}
        ref={graphRef}
        graphData={activeGraphData}
        width={containerWidth}
        height={HEIGHT}
        backgroundColor={bgColor}
        nodeCanvasObject={mode === 'clusters' ? clusterNodePaint : tweetNodePaint}
        nodeCanvasObjectMode={() => 'replace'}
        nodePointerAreaPaint={nodePointerAreaPaint}
        onNodeClick={mode === 'clusters' ? handleClusterClick : handleTweetClick}
        onNodeHover={setHovered}
        linkColor={() => linkColor}
        linkWidth={link =>
          mode === 'clusters'
            ? Math.max(0.5, (link.value || 1) * 0.7)
            : Math.max(0.5, (link.value || 0.5) * 2)
        }
        onEngineStop={handleEngineStop}
        cooldownTicks={200}
        d3AlphaDecay={0.02}
        d3VelocityDecay={0.35}
        enableNodeDrag
        enableZoomInteraction
      />
    </div>
  );
}
